// ─── Report code roles ────────────────────────────────────────────────────────
// GH       → Level 0 — Accordion item (non-editable, shows grand total)
// SH       → Level 1 — Collapsible section header (non-editable, shows subtotal)
// SSH      → Level 2 — Collapsible sub-section header (non-editable, shows subtotal)
// VP       → Level 3 — Editable leaf row
// VP/IP    → Level 3 — Editable leaf row
// IP       → Level 3 — Editable leaf row
// SST / ST / GT → SKIP (computed totals, never rendered)
// ─────────────────────────────────────────────────────────────────────────────

export const SKIP_CODES = new Set(['SST', 'ST', 'GT','RGT'])
export const EDITABLE_CODES = new Set(['VP', 'VP/IP', 'IP'])

export interface MonthDetail {
  month: string
  month_count: number
  year: string
  amount: number
  is_editable: number
  [key: string]: any
}

// Raw shape returned by the API (already nested)
export interface ApiNode {
  name: {
    id: number
    account: number | string
    name: string
    description?: string
    report_code: string
    parent_id: number | null
    financial_statement?: string
    parenthisys_control?: string
  }
  children: ApiNode[]
  details?: MonthDetail[]
  [key: string]: any
}

// Internal node used throughout the UI
export interface TreeNode {
  id: number
  account: string
  accountName: string
  reportCode: string
  details: MonthDetail[]
  children: TreeNode[]
  isEditable: boolean
  formIndex: number   // -1 until assigned by flattenForForm
  depth: number
  [key: string]: any
}

// ─── Convert API response → internal TreeNode tree ───────────────────────────
// Recursively maps ApiNode[] → TreeNode[], skipping SST / ST / GT nodes.
export function convertTree(apiNodes: ApiNode[], depth = 0): TreeNode[] {
  return apiNodes
    .filter((node) => !SKIP_CODES.has(node.name.report_code))
    .map((node) => ({
      id: node.name.id,
      account: String(node.name.account),
      accountName: node.name.name,
      reportCode: node.name.report_code,
      details: node.details ?? [],
      children: convertTree(node.children ?? [], depth + 1),
      isEditable: EDITABLE_CODES.has(node.name.report_code),
      formIndex: -1,
      depth,
    }))
}

// ─── Flatten editable leaves depth-first, assign formIndex ───────────────────
// Only VP / VP-IP / IP nodes go into useFieldArray.
// Returns the ordered list used to call formAppend.
export function flattenForForm(roots: TreeNode[]): TreeNode[] {
  const result: TreeNode[] = []
  let idx = 0

  function traverse(nodes: TreeNode[]) {
    nodes.forEach((node) => {
      if (node.isEditable) {
        node.formIndex = idx++
        result.push(node)
      }
      traverse(node.children)
    })
  }
  traverse(roots)
  return result
}

// ─── Flat virtual list types ──────────────────────────────────────────────────
export type FlatItemType = 'sh-header' | 'ssh-header' | 'leaf-row'

export interface FlatVirtualItem {
  type: FlatItemType
  node: TreeNode
}

// ─── Flatten visible children of one GH node for TanStack virtualizer ────────
// collapsedSet: node IDs whose children are hidden
export function flattenGroupChildren(
  ghNode: TreeNode,
  collapsedSet: Set<number>
): FlatVirtualItem[] {
  const result: FlatVirtualItem[] = []

  function traverse(nodes: TreeNode[]) {
    nodes.forEach((node) => {
      if (node.reportCode === 'SH') {
        result.push({ type: 'sh-header', node })
        if (!collapsedSet.has(node.id)) traverse(node.children)
      } else if (node.reportCode === 'SSH') {
        result.push({ type: 'ssh-header', node })
        if (!collapsedSet.has(node.id)) traverse(node.children)
      } else if (node.isEditable) {
        result.push({ type: 'leaf-row', node })
      }
    })
  }

  traverse(ghNode.children)
  return result
}

// ─── Sum all editable leaf descendants of a node ─────────────────────────────
export function sumDescendants(
  node: TreeNode,
  getTotal: (formIndex: number) => number
): number {
  if (node.isEditable) return getTotal(node.formIndex)
  return node.children.reduce(
    (acc, child) => acc + sumDescendants(child, getTotal),
    0
  )
}

// ─── Collect all editable formIndexes under a node ───────────────────────────
export function collectFormIndexes(node: TreeNode): number[] {
  if (node.isEditable) return [node.formIndex]
  return node.children.flatMap(collectFormIndexes)
}

// Helper: Find a node by ID anywhere in the nested tree
export function findSubtreeById(nodes: TreeNode[], targetId: number | string): TreeNode[] {
  for (const node of nodes) {
    // If the node itself matches, return it as a root
    if (node.id === targetId) {
      return [node]
    }

    // Otherwise, check inside its children recursively
    if (node.children && node.children.length > 0) {
      const matchInChildren = findSubtreeById(node.children, targetId)
      if (matchInChildren.length > 0) {
        // Return a shallow copy of the parent containing only the matched branch
        return [{
          ...node,
          children: matchInChildren
        }]
      }
    }
  }
  return []
}